/**
 * "Weddings De Goa" — the quote and invoice PDF layouts for the Weddings De
 * Goa brand.
 *
 * They are ordinary document templates (`DocumentTemplate` rows, HTML with
 * `{{merge.tokens}}`), added *beside* an organisation's own — never as its
 * default, never replacing one. An organisation's documents print exactly as
 * they did until somebody chooses "Weddings De Goa — Quote" / "— Invoice" in
 * the PDF template selector.
 *
 * The business identity (logo, name, address, contact details) and the Terms &
 * Conditions are the Weddings De Goa brand's own, copied from the reference
 * quotation and fixed on the template. Everything about the customer and the
 * transaction — numbers, names, lines, totals — is a token resolved from the
 * quote or invoice at render time.
 *
 * Two names rather than one, because template names are unique per
 * organisation; the selector for each document kind lists only its own, so a
 * quote's dropdown reads "Weddings De Goa — Quote".
 *
 * Layout notes for the PDF renderer (Chromium print):
 *  - `thead { display: table-header-group }` repeats the column headings on
 *    every page of a long document;
 *  - rows never split across a page, the totals block and the signature stay
 *    together, and the closing sections are never stranded alone;
 *  - the footer is `position: fixed`, which Chromium repeats on each page,
 *    with the page margin reserving room beneath the content for it.
 */

import { PHOTOBOOTH_GOA_LOGO_DATA_URI } from './photobooth-goa-logo.js';
import { WEDDINGS_DE_GOA_LOGO_DATA_URI } from './weddings-de-goa-logo.js';

export const WEDDINGS_DE_GOA_QUOTE_NAME = 'Weddings De Goa — Quote';
export const WEDDINGS_DE_GOA_INVOICE_NAME = 'Weddings De Goa — Invoice';
export const PHOTOBOOTH_GOA_QUOTE_NAME = 'PhotoboothGoa - Quote';
export const PHOTOBOOTH_GOA_INVOICE_NAME = 'PhotoboothGoa - Invoice';
export const WEDDINGS_DE_GOA_CREDIT_NOTE_NAME = 'Weddings De Goa — Credit Notes';
export const PHOTOBOOTH_GOA_CREDIT_NOTE_NAME = 'PhotoboothGoa - Credit Notes';

/** Styles both layouts share. Warm neutrals, one gold accent, hairline rules. */
const SHARED_STYLES = `
  @page { size: A4; margin: 14mm 15mm 14mm 15mm; @bottom-right { content: counter(page); font-size: 8.5px; color: #7a7064; } }
  :root {
    --dg-ink: #2a2622;
    --dg-muted: #7a7064;
    --dg-gold: #a98456;
    --dg-gold-soft: #f3ece1;
    --dg-sand: #faf7f2;
    --dg-line: #e6ddd0;
    --dg-serif: 'Cormorant Garamond', Georgia, 'Liberation Serif', 'Times New Roman', serif;
  }
  body { color: var(--dg-ink); font-size: 10.5px; line-height: 1.55; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .dg-muted { color: var(--dg-muted); }

  .dg-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .dg-brand { max-width: 52%; }
  .dg-logo { display: block; max-height: 84px; max-width: 250px; width: auto; margin: 0 0 10px; object-fit: contain; }
  .dg-brand-name { font-family: var(--dg-serif); font-size: 17px; font-weight: 600; letter-spacing: 0.04em; margin: 0 0 3px; }
  .dg-org-address, .dg-org-contact, .dg-org-tax { color: var(--dg-muted); font-size: 10px; }
  .dg-org-address:empty, .dg-org-contact:empty, .dg-org-tax:empty { display: none; }

  .dg-title { text-align: right; min-width: 44%; }
  .dg-title h1 { font-family: var(--dg-serif); font-weight: 500; font-size: 29px; letter-spacing: 0.22em; text-transform: uppercase; margin: 0; line-height: 1.1; }
  .dg-number { color: var(--dg-gold); font-weight: 700; letter-spacing: 0.06em; margin: 5px 0 12px; font-size: 11.5px; }
  .dg-meta { margin: 0; }
  .dg-meta-row { display: flex; justify-content: flex-end; gap: 14px; padding: 2px 0; }
  .dg-meta-row dt { color: var(--dg-muted); margin: 0; }
  .dg-meta-row dd { margin: 0; min-width: 96px; text-align: right; font-weight: 600; }

  .dg-orn { display: flex; align-items: center; gap: 10px; margin: 16px 0 18px; }
  .dg-orn::before, .dg-orn::after { content: ''; height: 1px; flex: 1; background: var(--dg-line); }
  .dg-orn span { width: 6px; height: 6px; background: var(--dg-gold); transform: rotate(45deg); }

  .dg-label { font-family: var(--dg-serif); font-size: 10.5px; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: var(--dg-gold); margin: 0 0 6px; }
  .dg-parties { display: flex; justify-content: space-between; gap: 28px; margin-bottom: 16px; }
  .dg-party { flex: 1.2; }
  .dg-subject-box { flex: 1; }
  .dg-party-name { font-size: 13px; font-weight: 700; margin-bottom: 1px; }
  .dg-party-contact { font-weight: 600; }
  .dg-party-address, .dg-party-line { color: #4a443d; }
  .dg-subject { font-size: 11.5px; font-weight: 600; }
  .dg-subject-box:has(.dg-subject:empty) { display: none; }

  table { border-collapse: collapse; width: 100%; }
  .dg-lines { margin-top: 6px; counter-reset: dgrow; }
  .dg-lines thead { display: table-header-group; }
  .dg-lines th { background: var(--dg-gold-soft); color: var(--dg-ink); font-size: 8.5px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; text-align: right; padding: 8px 6px; border-top: 1px solid var(--dg-gold); border-bottom: 1px solid var(--dg-gold); }
  .dg-lines th.dg-l, .dg-lines td.dg-l { text-align: left; }
  .dg-lines td { padding: 10px 6px; vertical-align: top; text-align: right; border-bottom: 1px solid var(--dg-line); font-variant-numeric: tabular-nums; }
  .dg-lines tr { break-inside: avoid; page-break-inside: avoid; }
  .dg-lines td.dg-n { width: 26px; text-align: left; color: var(--dg-gold); font-weight: 700; }
  .dg-lines tbody tr { counter-increment: dgrow; }
  .dg-lines td.dg-n::before { content: counter(dgrow); }
  .dg-lines td.dg-desc { text-align: left; width: 100%; overflow-wrap: anywhere; }
  .dg-lines td.dg-desc p { margin: 0 0 5px; }
  .dg-lines td.dg-desc p:last-child { margin-bottom: 0; }
  .dg-lines td.dg-desc ul, .dg-lines td.dg-desc ol { margin: 3px 0 0 16px; padding: 0; }
  .dg-lines td.dg-num { white-space: nowrap; }

  .dg-closing { display: flex; justify-content: space-between; align-items: flex-start; gap: 28px; margin-top: 16px; break-inside: avoid; page-break-inside: avoid; }
  .dg-words { flex: 1; padding-top: 4px; }
  .dg-words-text { font-style: italic; font-weight: 700; }
  .dg-summary { width: 270px; flex: none; }
  .dg-summary th { text-align: left; font-weight: 400; color: var(--dg-muted); padding: 4px 0; }
  .dg-summary td { text-align: right; padding: 4px 0; font-variant-numeric: tabular-nums; }
  .dg-summary tr.dg-total th, .dg-summary tr.dg-total td { border-top: 1px solid var(--dg-gold); padding-top: 8px; color: var(--dg-ink); font-weight: 700; font-size: 12.5px; }
  .dg-summary tr.dg-paid td { color: #a33a30; }
  .dg-summary tr.dg-balance th, .dg-summary tr.dg-balance td { background: var(--dg-gold-soft); color: var(--dg-ink); font-weight: 700; font-size: 12.5px; padding: 7px 8px; }
  .dg-summary tr.dg-base th, .dg-summary tr.dg-base td { font-size: 9.5px; color: var(--dg-muted); }

  .dg-terms { margin-top: 20px; color: #4a443d; font-size: 9.8px; line-height: 1.45; }
  .dg-terms-title { font-size: 10.5px; font-weight: 700; color: var(--dg-ink); margin: 0 0 10px; break-after: avoid; }
  .dg-term { margin: 0 0 10px; }
  .dg-term-heading { font-weight: 700; color: var(--dg-ink); break-after: avoid; }
  .dg-term p { margin: 0 0 8px; break-inside: avoid; white-space: pre-wrap; }
  .dg-term p:last-child { margin-bottom: 0; }

  .dg-sign { margin-top: 26px; display: flex; justify-content: flex-end; break-inside: avoid; page-break-inside: avoid; }
  .dg-sign-box { width: 240px; height: 74px; border-bottom: 1px solid var(--dg-ink); position: relative; }
  .dg-sign-caption { width: 240px; text-align: center; color: var(--dg-muted); font-size: 9.5px; letter-spacing: 0.12em; text-transform: uppercase; padding-top: 5px; }

  /* The whole page is one table so its footer is a real tfoot: Chromium repeats a
     tfoot at the foot of every page *and reserves room for it*, which a fixed
     footer does not (content would run underneath it). min-height pushes it to
     the bottom of a short, single-page document. */
  .dg-frame { width: 100%; min-height: calc(100vh - 2px); }
  .dg-frame > tbody > tr > td, .dg-frame > tfoot > tr > td { padding: 0; vertical-align: top; }
  .dg-frame > tbody > tr > td { height: 100%; }
  .dg-foot { text-align: center; border-top: 1px solid var(--dg-line); margin-top: 14px; padding-top: 7px; color: var(--dg-muted); font-size: 9px; break-inside: avoid; }
  .dg-foot-thanks { font-family: var(--dg-serif); font-size: 12px; font-style: italic; color: var(--dg-gold); margin-bottom: 1px; }
  .dg-foot-note:empty { display: none; }
`;

/**
 * The business identity printed on both layouts. Fixed text copied from the
 * supplied reference quotation (QT-WHN002553) — not merge tokens — because the
 * identity belongs to the Weddings De Goa brand, not to whichever organisation
 * happens to own the document: the CRM's own name must never surface here.
 */
export const WEDDINGS_DE_GOA_BUSINESS_NAME = 'Weddings De Goa';
export const WEDDINGS_DE_GOA_ADDRESS_LINES = [
  'Ucassaim , Pello Vaddo,',
  'Mapusa - Bardez 403508,',
  'Goa India',
] as const;
export const WEDDINGS_DE_GOA_CONTACT_LINES = [
  'Email: weddingsdegoa@whnnext.com',
  'www.weddingsdegoa.com',
] as const;

const htmlEscape = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** A brand's fixed identity: logo, name, address, contact lines and its Terms & Conditions. */
interface Brand {
  name: string;
  logo: string;
  address: readonly string[];
  contact: readonly string[];
  /** An unheaded paragraph before the first term, when the brand's terms open with one. */
  termsIntro?: string;
  terms: readonly { heading?: string; paragraphs: string[][] }[];
}

const HEAD = (brand: Brand, title: string) => `
<header class="dg-head">
  <div class="dg-brand">
    <img class="dg-logo" src="${brand.logo}" alt="${htmlEscape(brand.name)}" />
    <div class="dg-brand-name">${htmlEscape(brand.name)}</div>
    <div class="dg-org-address">${brand.address.map(htmlEscape).join('<br />')}</div>
    <div class="dg-org-contact">${brand.contact.map(htmlEscape).join('<br />')}</div>
  </div>
  <div class="dg-title">
    <h1>${title}</h1>
    <div class="dg-number"># {{document.number}}</div>
    <dl class="dg-meta">{{document.metaHtml}}</dl>
  </div>
</header>
<div class="dg-orn"><span></span></div>`;

const FRAME_OPEN = `<table class="dg-frame"><tbody><tr><td>`;

const FRAME_CLOSE = (brand: Brand) => `</td></tr></tbody><tfoot><tr><td>
<footer class="dg-foot">
  <div>${brand.contact.map(htmlEscape).join(' &middot; ')}</div>
</footer>
</td></tr></tfoot></table>`;

/**
 * Terms & Conditions of the Weddings De Goa reference quotation, word for word
 * — spelling slips and all ("Wedddingsdegoa", "1 years", "clients listing") —
 * in the reference's order, headings and line breaks. Each section is a
 * heading and its paragraphs; a paragraph is a list of lines, because the
 * reference puts some sentences on their own line inside one block.
 *
 * Fixed on the template, so a document printed with it always carries these
 * terms: neither the organisation's default terms nor anything stored on the
 * quote or invoice replaces them.
 */
export const WEDDINGS_DE_GOA_TERMS: readonly { heading?: string; paragraphs: string[][] }[] = [
  {
    heading: 'Listing Content:',
    paragraphs: [
      [
        'The vendor is responsible for providing accurate and up-to-date information for their listing.',
        'Weddingsdegoa reserves the right to edit or remove any content that violates our policies.',
      ],
    ],
  },
  {
    heading: 'Listing Duration:',
    paragraphs: [
      [
        "The Listing on Weddingsdegoa shall remain online for a minimum of 1 years. After this period, the listing may be relocated or modified based on the company's response and activity. While we may continue to maintain the listing beyond the initial 1 years, changes to its placement may occur if we do not receive a response regarding the continuation of the service.",
        'Additional services can be added with an additional charge.',
      ],
    ],
  },
  {
    heading: 'Rights and Permissions:',
    paragraphs: [
      [
        'By submitting content for the listing, the vendor grants Weddingsdegoa the right to display, reproduce, and distribute the provided information on our website.',
      ],
    ],
  },
  {
    heading: 'Cancellation & Refund Policy:',
    paragraphs: [
      [
        'Cancellation of listing will be carried out with no refund of the amount.',
        'Once services are activated No refund will be provided.',
        'No refund policy.',
      ],
    ],
  },
  {
    heading: 'Social Media Posting:',
    paragraphs: [
      [
        "Social media posting for your listing will commence in the month following the listing's activation on our website.",
        'If two social media posts are scheduled per month, the first post will be published within the initial 15 days, and the second post will follow in the subsequent 15 days of the month.',
        'Social Media posting will be kept on hold if the quality of pictures is poor.',
      ],
    ],
  },
  {
    heading: 'Modification of Terms:',
    paragraphs: [
      [
        'Weddingsdegoa reserves the right to modify these terms and conditions at any time, without notice.',
      ],
      [
        'We value the professionalism put in by every vendor. In case we receive feedbacks from clients that a vendor has given below wrong info or wrongly portrayed his profile, weddingsdegoa has the right to take down the listing.',
      ],
      [
        'We believe the images and content provided by the wedding service provider  represents and warrants that he or she owns or has the necessary experience, licenses, rights, to their business details. The information provided by suppliers will be deemed correct as accepted.',
      ],
      [
        'Weddingsdegoa reserves the right to modify, the content, layout, photos, the design, presentation and/or configuration of the Website at our discretion.',
      ],
      [
        'The Photos ,Videos and content provided for listing is the sole responsibility of the client to get required permissions and approvals from the content provider. Weddingsdegoa is not responsible for the content provided.',
      ],
      [
        'Add on services shall be provided to clients that opt for add on packages on request. Wedddingsdegoa holds the right to change any listing packages depending upon the requirements.',
      ],
      [
        'Weddingsdegoa reserve the right to change any category if deemed more suitable. We reserve the right to change or amend the content at any time.',
      ],
      [
        'We reserve the right to add categories where we see appropriate. We reserve the rights to delete a category if we do not receive any listings or add a category where necessary.',
      ],
      [
        'We reserve the right to withdraw a supplier from the website if deemed inappropriate for any reason.',
      ],
      [
        'All decisions relating to eligibility, categories and listings are made at the sole discretion of weddingsdegoa and its staff and are final.',
      ],
      [
        'Weddingsdegoa  and all its Intellectual Property remains the sole copyright of WHN Next. These rights will be enforced under the jurisdiction of Indian Law and all claims will be pursued as appropriate.',
      ],
      [
        'These terms and conditions shall be governed in all respects by the laws of Indian judiciary and both parties agree to submit to the exclusive jurisdiction of the courts of India.',
      ],
      [
        'Any disputes arising from or related to our Services shall be subject to the jurisdiction of the courts of India.',
      ],
      [
        'Violation of any terms and conditions will lead to termination or deletion of the clients listing after reviewing and clients.',
      ],
    ],
  },
];

const termsHtml = (brand: Brand) => `
<section class="dg-terms">
  <h2 class="dg-terms-title">TERMS &amp; CONDITIONS</h2>${brand.termsIntro ? `\n  <div class="dg-term"><p>${htmlEscape(brand.termsIntro)}</p></div>` : ''}
${brand.terms
  .map(
    (section) =>
      `  <div class="dg-term">${section.heading ? `<div class="dg-term-heading">${htmlEscape(section.heading)}</div>` : ''}${section.paragraphs
        .map((lines) => `<p>${lines.map(htmlEscape).join('<br />')}</p>`)
        .join('')}</div>`,
  )
  .join('\n')}
</section>`;

const SIGNATURE = `
<section class="dg-sign">
  <div>
    <div class="dg-sign-box"></div>
    <div class="dg-sign-caption">Authorized Signature</div>
  </div>
</section>`;

const quoteBody = (brand: Brand) => `${FRAME_OPEN}${HEAD(brand, 'Quotation')}
<section class="dg-parties">
  <div class="dg-party">
    <h2 class="dg-label">Quotation for</h2>
    {{billTo.detailsHtml}}
  </div>
  <div class="dg-subject-box">
    <h2 class="dg-label">Subject</h2>
    <div class="dg-subject">{{document.subject}}</div>
  </div>
</section>

<table class="dg-lines">
  <thead>
    <tr>
      <th class="dg-l">#</th>
      <th class="dg-l">Item &amp; Description</th>
      <th>Qty</th>
      <th>Rate</th>
      <th>Discount</th>
      <th>Tax</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {{#each lines}}
    <tr>
      <td class="dg-n"></td>
      <td class="dg-desc">{{description}}</td>
      <td class="dg-num">{{quantity}}</td>
      <td class="dg-num">{{unitPrice}}</td>
      <td class="dg-num">{{discountPercent}}</td>
      <td class="dg-num">{{taxRate}}</td>
      <td class="dg-num"><strong>{{lineTotal}}</strong></td>
    </tr>
    {{/each}}
  </tbody>
</table>

<section class="dg-closing">
  <div class="dg-words"></div>
  <table class="dg-summary">{{document.summaryHtml}}</table>
</section>
${termsHtml(brand)}
${SIGNATURE}
${FRAME_CLOSE(brand)}`;

const invoiceBody = (brand: Brand) => `${FRAME_OPEN}${HEAD(brand, 'Invoice')}
<section class="dg-parties">
  <div class="dg-party">
    <h2 class="dg-label">Bill to</h2>
    {{billTo.detailsHtml}}
  </div>
  <div class="dg-subject-box">
    <h2 class="dg-label">Subject</h2>
    <div class="dg-subject">{{document.subject}}</div>
  </div>
</section>

<table class="dg-lines">
  <thead>
    <tr>
      <th class="dg-l">#</th>
      <th class="dg-l">Item &amp; Description</th>
      <th>Qty</th>
      <th>Rate</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {{#each lines}}
    <tr>
      <td class="dg-n"></td>
      <td class="dg-desc">{{description}}</td>
      <td class="dg-num">{{quantity}}</td>
      <td class="dg-num">{{unitPrice}}</td>
      <td class="dg-num"><strong>{{lineTotal}}</strong></td>
    </tr>
    {{/each}}
  </tbody>
</table>

<section class="dg-closing">
  <div class="dg-words">
    <h2 class="dg-label">Total in words</h2>
    <div class="dg-words-text">{{document.totalInWords}}</div>
  </div>
  <table class="dg-summary">{{document.summaryHtml}}</table>
</section>
${termsHtml(brand)}
${SIGNATURE}
${FRAME_CLOSE(brand)}`;

const creditNoteBody = (brand: Brand) => `${FRAME_OPEN}${HEAD(brand, 'Credit Note')}
<section class="dg-parties">
  <div class="dg-party">
    <h2 class="dg-label">Credit note for</h2>
    {{billTo.detailsHtml}}
  </div>
  <div class="dg-subject-box">
    <h2 class="dg-label">Reason</h2>
    <div class="dg-subject">{{document.subject}}</div>
  </div>
</section>

<table class="dg-lines">
  <thead>
    <tr>
      <th class="dg-l">#</th>
      <th class="dg-l">Item &amp; Description</th>
      <th>Qty</th>
      <th>Rate</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {{#each lines}}
    <tr>
      <td class="dg-n"></td>
      <td class="dg-desc">{{description}}</td>
      <td class="dg-num">{{quantity}}</td>
      <td class="dg-num">{{unitPrice}}</td>
      <td class="dg-num"><strong>{{lineTotal}}</strong></td>
    </tr>
    {{/each}}
  </tbody>
</table>

<section class="dg-closing">
  <div class="dg-words">
    <h2 class="dg-label">Total in words</h2>
    <div class="dg-words-text">{{document.totalInWords}}</div>
  </div>
  <table class="dg-summary">{{document.summaryHtml}}</table>
</section>
${termsHtml(brand)}
${SIGNATURE}
${FRAME_CLOSE(brand)}`;

const WEDDINGS_DE_GOA_BRAND: Brand = {
  name: WEDDINGS_DE_GOA_BUSINESS_NAME,
  logo: WEDDINGS_DE_GOA_LOGO_DATA_URI,
  address: WEDDINGS_DE_GOA_ADDRESS_LINES,
  contact: WEDDINGS_DE_GOA_CONTACT_LINES,
  terms: WEDDINGS_DE_GOA_TERMS,
};

export const WEDDINGS_DE_GOA_QUOTE_BODY = quoteBody(WEDDINGS_DE_GOA_BRAND);
export const WEDDINGS_DE_GOA_INVOICE_BODY = invoiceBody(WEDDINGS_DE_GOA_BRAND);
export const WEDDINGS_DE_GOA_CREDIT_NOTE_BODY = creditNoteBody(WEDDINGS_DE_GOA_BRAND);

/**
 * Photobooth Goa: the same layout, styling and colours as Weddings De Goa, with
 * its own logo, identity and Terms & Conditions, which are the brand's own text
 * (copied from its supplied quotation and invoice). Everything about the
 * customer and the transaction stays a merge token, resolved at render time.
 */
export const PHOTOBOOTH_GOA_BUSINESS_NAME = 'Photobooth Goa';
export const PHOTOBOOTH_GOA_ADDRESS_LINES = WEDDINGS_DE_GOA_ADDRESS_LINES;
export const PHOTOBOOTH_GOA_CONTACT_LINES = ['Email: photoboothgoa@whnnext.com', 'www.photoboothgoa.com'] as const;
export const PHOTOBOOTH_GOA_TERMS_INTRO =
  'Kindly read through the below terms and conditions before booking the Photo-booth goa.';
export const PHOTOBOOTH_GOA_TERMS: readonly { heading?: string; paragraphs: string[][] }[] = [
  {
    heading: 'BOOKING',
    paragraphs: [
      [
        'The client may request to book the Photo-booth',
        'via email: photoboothgoa@whnnext.com or',
        'via Mobile Booking number +919146970951 or',
        'via Photobooth Goa Website https://photoboothgoa.com/',
        'The bookings must be done prior to 30days of the event.',
      ],
    ],
  },
  {
    heading: 'AVAILABILITY',
    paragraphs: [
      [
        'The availability of the Photo-Booth can be requested on the above provided contact details. Our Team shall assist you in checking the availability.',
      ],
    ],
  },
  {
    heading: 'REQUIREMENTS – POWER & BACKDROP',
    paragraphs: [
      [
        'The client has to provide the photobooth with a backdrop, also a 230VAC Power supply socket connection must be made available at the location.',
      ],
    ],
  },
  { heading: 'PAYMENT TERMS', paragraphs: [] },
  {
    heading: 'ADVANCE PAYMENTS',
    paragraphs: [
      [
        'Advance payment of 75% must be made at the time of booking the event. The event will be confirmed only once the advance has been received.',
      ],
    ],
  },
  {
    heading: 'FINAL PAYMENTS',
    paragraphs: [['The Balance Payments must be carried out 3 Days before the event or on the day of the event.']],
  },
  {
    heading: 'MODE OF PAYMENT',
    paragraphs: [
      [
        'The Payments are accepted in Gpay, Cash or Online Bank Transfer. Once payments are carried out kindly send us the screenshot or transaction details of the payments carried out.',
      ],
    ],
  },
  {
    heading: 'TIMELINES AND DELAYS',
    paragraphs: [
      [
        'Punctuality: The service provider will strive to adhere strictly to the agreed-upon time frame.',
        'Minor Delays: Minor delays of up to 30 minutes may be understandable due to unforeseen circumstances.',
        'Excessive Delays: Delays exceeding 30 minutes may result in additional charges.',
        'Extended Service: If the client requires an extension of the service by one hour, an additional fee of INR 5,000 will be applicable.',
      ],
    ],
  },
  {
    heading: 'CANCELLATION OF EVENTS',
    paragraphs: [
      [
        'In the event that the client decides to cancel the event for any reason, Then Photo-Booth Goa (WHN NEXT) shall be entitled to retain 50% of the Advance amount. Notice of cancellation of the event must be sent via email to weddingsdegoa@whnnext.com by the client. If such cancellation occurs less than 30 days prior to the date of the event, the full Advance amount will be retained. If the client has paid the total fee upfront – the same terms apply.',
      ],
      [
        'If the client decides, for any reason, to postpone or change the date of the event, official email must be sent to weddingsdegoa@whnnext.com',
      ],
      [
        'In order for us to consider accommodating the new event date at the original price, we must be informed of the new date within 30 days of the notice of the date change; the new date must be no more than 12 months after the original event date; and the decision whether to accommodate the new date is subject to the availability.',
      ],
      [
        'Photo-Booth Goa shall be entitled to retain the non-refundable, non-transferable fee (50% of the total booking) regardless of when the written notice is given.',
      ],
      [
        'If we cannot accommodate the new date or the written notice of date change is made by the client less than 30 days before the event date, the client shall be responsible for the remaining balance of the total fee.The decision to provide our services for a postponed or rescheduled event by the client is subject to the Availability of our Photobooth.',
      ],
    ],
  },
  {
    heading: 'RETURNS AND REFUNDS',
    paragraphs: [
      [
        'Incase of any Refunds agreed, the time frame will be a minimum of 30 days from the agreed date. The Transfers will only be made via Bank transactions with an Indian Bank account. Gpay will also be used as optional for refund transfer. The bank account details,Gpay details must be provided by the client.',
      ],
    ],
  },
  {
    heading: 'TECHNICAL FAILURE',
    paragraphs: [
      [
        'Worst case scenario – Incase due to technical error (Worst of the worst Case) the Photobooth Goa is unable to perform its regular service at the event, Then we would try with the Regular photo booth with DSLR Camera to shoot photos. The print outs shall be given.',
      ],
      [
        'In case we are unable to use the photo booth due to technical reasons, then a refund of 100% shall be given to the client within a period of 30 days from the event.',
      ],
    ],
  },
  {
    heading: 'DATA USAGE & PRIVACY',
    paragraphs: [
      [
        "The Digital Data(content, photographs and videos) clicked by the digital mirror Goa will be stored on our Digital cloud for a period of 3 months. This digital data may be used for promotional purposes on social media, websites or other ad forms of whn next platforms. The client doesn't want any data to be used for any promotional material on any social media platforms as mentioned by the client during the booking. The data will be shared to the client on a Google drive link - the data will be needed to be downloaded by the client and mentioned once done after which the link will be deleted by photo booth goa.",
      ],
    ],
  },
  {
    heading: 'DISCLAIMER',
    paragraphs: [
      [
        'Photo-Booth Goa absolutely disclaims all warranties, expressed or implied, including, but not limited to the services offered . It gives no warranty that the documents, goods or services will be free of errors, or that defects will be corrected, or that our website or its server is free of viruses or any other harmful components.',
      ],
      [
        'Whilst we, at all times endeavour to have the most accurate, reliable and up-to-date information on our website, we do not warrant or make any representations regarding the use or the result of the use of any document, product, service, link or information in its website or as to their correctness, suitability, accuracy, reliability, or otherwise.',
      ],
      [
        'It is your sole responsibility and not the responsibility of Photo-Booth Goa to bear any and all costs of servicing, repairs, or correction.',
      ],
      [
        'Some of the above may not apply to you but you must ensure you are aware of any risk you may be taking by using this website or any products or services that may be offered through it. It is your responsibility to do so.',
      ],
    ],
  },
];


const PHOTOBOOTH_GOA_BRAND: Brand = {
  name: PHOTOBOOTH_GOA_BUSINESS_NAME,
  logo: PHOTOBOOTH_GOA_LOGO_DATA_URI,
  address: PHOTOBOOTH_GOA_ADDRESS_LINES,
  contact: PHOTOBOOTH_GOA_CONTACT_LINES,
  termsIntro: PHOTOBOOTH_GOA_TERMS_INTRO,
  terms: PHOTOBOOTH_GOA_TERMS,
};

export const PHOTOBOOTH_GOA_QUOTE_BODY = quoteBody(PHOTOBOOTH_GOA_BRAND);
export const PHOTOBOOTH_GOA_INVOICE_BODY = invoiceBody(PHOTOBOOTH_GOA_BRAND);
export const PHOTOBOOTH_GOA_CREDIT_NOTE_BODY = creditNoteBody(PHOTOBOOTH_GOA_BRAND);

export interface BuiltInDocumentTemplate {
  name: string;
  description: string;
  appliesTo: 'QUOTE' | 'INVOICE' | 'CREDIT_NOTE';
  body: string;
  styles: string;
}

/**
 * Templates added to every organisation, alongside its own. Never default,
 * never replacing anything: see the note at the top of the file.
 */
export const BUILT_IN_DOCUMENT_TEMPLATES: readonly BuiltInDocumentTemplate[] = [
  {
    name: WEDDINGS_DE_GOA_QUOTE_NAME,
    description:
      'The Weddings De Goa quotation: warm, minimal, with the logo, customer, itemised services, totals, terms and signature.',
    appliesTo: 'QUOTE',
    body: WEDDINGS_DE_GOA_QUOTE_BODY,
    styles: SHARED_STYLES,
  },
  {
    name: WEDDINGS_DE_GOA_INVOICE_NAME,
    description:
      'The Weddings De Goa invoice: bill-to, itemised services, total in words, payment made and balance due, and a signature area.',
    appliesTo: 'INVOICE',
    body: WEDDINGS_DE_GOA_INVOICE_BODY,
    styles: SHARED_STYLES,
  },
  {
    name: WEDDINGS_DE_GOA_CREDIT_NOTE_NAME,
    description:
      'The Weddings De Goa credit note: the invoice layout, titled Credit Note, for the invoice it credits.',
    appliesTo: 'CREDIT_NOTE',
    body: WEDDINGS_DE_GOA_CREDIT_NOTE_BODY,
    styles: SHARED_STYLES,
  },
  // Photobooth Goa: the same layout, styling and colours, with its own logo,
  // identity and Terms & Conditions.
  {
    name: PHOTOBOOTH_GOA_QUOTE_NAME,
    description: 'The Photobooth Goa quotation, in the Weddings De Goa layout, with its own logo and terms.',
    appliesTo: 'QUOTE',
    body: PHOTOBOOTH_GOA_QUOTE_BODY,
    styles: SHARED_STYLES,
  },
  {
    name: PHOTOBOOTH_GOA_INVOICE_NAME,
    description: 'The Photobooth Goa invoice, in the Weddings De Goa layout, with its own logo and terms.',
    appliesTo: 'INVOICE',
    body: PHOTOBOOTH_GOA_INVOICE_BODY,
    styles: SHARED_STYLES,
  },
  {
    name: PHOTOBOOTH_GOA_CREDIT_NOTE_NAME,
    description: 'The Photobooth Goa credit note, titled Credit Note, for the invoice it credits.',
    appliesTo: 'CREDIT_NOTE',
    body: PHOTOBOOTH_GOA_CREDIT_NOTE_BODY,
    styles: SHARED_STYLES,
  },
];
